import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const Stars = ({ value, size = 14 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-[#B88A2E] text-[#B88A2E]" : "text-[#c9c2b4]"} />
    ))}
  </span>
);

function ReviewForm({ slug, mine, onSaved }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(mine?.rating || 0);
  const [text, setText] = useState(mine?.text || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRating(mine?.rating || 0); setText(mine?.text || ""); }, [mine]);
  if (!user || user === false) {
    return (
      <p className="text-sm text-[#171715]/70">
        <Link data-testid="review-signin-link" to={`/auth?next=/products/${slug}`} className="underline">Sign in</Link> to leave a review.
      </p>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, text: text.trim() || null });
      toast.success(mine ? "Your review was updated" : "Thanks — your review is live");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card-paper p-4" data-testid="review-form">
      <p className="text-sm font-medium mb-2">{mine ? "Update your review" : "Leave a review"}</p>
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} data-testid={`review-form-rating-${i}`} aria-label={`${i} star${i > 1 ? "s" : ""}`}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setRating(i)}>
            <Star size={22} className={i <= rating ? "fill-[#B88A2E] text-[#B88A2E]" : "text-[#c9c2b4]"} />
          </button>
        ))}
      </div>
      <textarea data-testid="review-form-text" className="input-line w-full min-h-[80px]" maxLength={600}
        placeholder="What did you think? (optional)" value={text} onChange={(e) => setText(e.target.value)} />
      <button data-testid="review-form-submit" className="btn-ink text-sm mt-3 px-5 min-h-[44px]"
        disabled={rating === 0 || busy} onClick={submit}>{busy ? "Posting…" : mine ? "Update review" : "Post review"}</button>
    </div>
  );
}

export function ReviewsSection({ slug, data, reload }) {
  if (!data) return null;
  return (
    <section className="mt-10 max-w-2xl" data-testid="product-reviews-section">
      <h2 className="text-2xl mb-1">Customer reviews</h2>
      {data.count > 0 ? (
        <p className="text-sm text-[#171715]/70 mb-4 flex items-center gap-2">
          <Stars value={data.average} /> <span className="font-mono">{data.average}</span> · {data.count} review{data.count > 1 ? "s" : ""}
        </p>
      ) : (
        <p className="text-sm text-[#171715]/70 mb-4">No reviews yet — be the first.</p>
      )}
      <div className="space-y-3 mb-6">
        {data.reviews.map((r) => (
          <div key={r.id} className="card-paper p-4 text-sm" data-testid={`review-item-${r.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-medium">{r.author}{r.verified && <span className="ml-2 text-[10px] font-mono uppercase text-[#A35847]">Verified pickup</span>}</span>
              <Stars value={r.rating} />
            </div>
            {r.text && <p className="mt-2 text-[#171715]/80">{r.text}</p>}
            <p className="text-xs text-[#171715]/50 mt-1">{new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
          </div>
        ))}
      </div>
      <ReviewForm slug={slug} mine={data.my_review} onSaved={reload} />
    </section>
  );
}

export function useReviews(slug) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => setData({ count: 0, average: null, reviews: [] }));
  }, [slug]);
  useEffect(() => { reload(); }, [reload]);
  return { data, reload };
}
